"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { TopicSelector } from "@/components/topic-selector";
import { LearningView } from "@/components/learning-view";
import { CustomTopicGenerator } from "@/components/custom-topic-generator";
import { AlphabetSection } from "@/components/alphabet-section";
import { PracticeSection } from "@/components/practice/practice-section";
import { OnboardingWizard } from "@/components/onboarding-wizard";
import { sampleTopics, type DialogTopic, type DialogNode } from "@/lib/dialog-data";
import { EMPTY_VOCABULARY } from "@/lib/constants";
import { Languages, BookOpen, MessageSquare, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { SettingsScreen } from "@/components/settings-screen";
import { useLanguage } from "@/lib/language-context";
import { useSettings } from "@/lib/settings-context";
import { getBaseLanguageCode } from "@/lib/language-alphabets";
import { getAlphabet } from "@/lib/alphabets";
import { useTranslations } from "@/lib/use-translations";
import { useAnalytics } from "@/lib/use-analytics";
import { useGenerationService, type GenerationParams } from "@/hooks/use-generation-service";
import type { CurriculumTopicMeta } from "@/lib/curriculum";
import type { LocaleCode, Difficulty, Richness } from "@/lib/api/client";

// Extended topic type with language metadata
interface TopicWithMeta extends DialogTopic {
  _generatedFor?: {
    targetLanguage: LocaleCode;
    nativeLanguage: LocaleCode;
  };
}

// Base info for building a live topic during streaming
interface StreamingTopicBase {
  id: string;
  title: string;
  originalPrompt: string;
  language: LocaleCode;
  difficulty: Difficulty;
  richness: Richness;
  replacingTopicId?: string;
}

type MainTab = "topics" | "practice" | "alphabet";
type View = "main" | "learning" | "custom-generator" | "settings";

const TOPICS_STORAGE_KEY = "converso-custom-topics";

export default function Home() {
  const [mainTab, setMainTab] = useState<MainTab>("topics");
  const [view, setView] = useState<View>("main");
  const [selectedTopic, setSelectedTopic] = useState<DialogTopic | null>(null);
  const [customTopics, setCustomTopics] = useState<TopicWithMeta[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [initialTopicData, setInitialTopicData] = useState<{
    topic: string;
    difficulty?: Difficulty;
    richness?: Richness;
    autoGenerate?: boolean;
    replacingTopicId?: string;
  } | null>(null);
  const { targetLanguage, nativeLanguage } = useLanguage();
  const { hasCompletedOnboarding, completeOnboarding, setAnalyticsOptOut, userProfile, tts } = useSettings();
  const t = useTranslations();
  const { track, events } = useAnalytics();
  const generation = useGenerationService();
  const [streamingTopicBase, setStreamingTopicBase] = useState<StreamingTopicBase | null>(null);
  const completionHandledRef = useRef<string | null>(null);

  // Load custom topics from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(TOPICS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setCustomTopics(parsed);
        }
      }
    } catch (e) {
      console.error("Failed to load custom topics:", e);
    }
    setIsLoaded(true);
  }, []);

  // Save custom topics to localStorage when they change
  useEffect(() => {
    if (isLoaded && customTopics.length > 0) {
      try {
        localStorage.setItem(TOPICS_STORAGE_KEY, JSON.stringify(customTopics));
      } catch (e) {
        console.error("Failed to save custom topics:", e);
      }
    }
  }, [customTopics, isLoaded]);

  // Get alphabet for selected language (translated to native language)
  const currentAlphabet = useMemo(() => {
    return getAlphabet(targetLanguage.code, nativeLanguage.code);
  }, [targetLanguage.code, nativeLanguage.code]);

  // Track previous language to detect changes
  const [prevLanguages, setPrevLanguages] = useState<{ target: string; native: string } | null>(null);

  // Detect language changes
  useEffect(() => {
    if (prevLanguages === null) {
      // Initial load, just store current languages
      setPrevLanguages({ target: targetLanguage.code, native: nativeLanguage.code });
      return;
    }

    const languageChanged =
      prevLanguages.target !== targetLanguage.code ||
      prevLanguages.native !== nativeLanguage.code;

    if (languageChanged) {
      // Mark topics that were generated for different languages as needing regeneration
      setCustomTopics((prev) =>
        prev.map((topic) => {
          if (topic._generatedFor) {
            const needsRegen =
              topic._generatedFor.targetLanguage !== targetLanguage.code ||
              topic._generatedFor.nativeLanguage !== nativeLanguage.code;
            if (needsRegen) {
              return { ...topic, _needsRegeneration: true };
            }
          }
          return topic;
        })
      );
      setPrevLanguages({ target: targetLanguage.code, native: nativeLanguage.code });
    }
  }, [targetLanguage.code, nativeLanguage.code, prevLanguages]);

  // Build a live DialogTopic from streaming state during generation
  const streamingTopic: DialogTopic | null = useMemo(() => {
    if (!streamingTopicBase) return null;
    // If the final topic is ready, use it directly (avoids flash of empty nodes)
    if (generation.generatedTopic) {
      // For curriculum adaptation, preserve the catalog's native-language title as the heading
      const isAdapt = streamingTopicBase.id.startsWith("adapt-");
      if (isAdapt && streamingTopicBase.title) {
        return { ...generation.generatedTopic, summary: streamingTopicBase.title };
      }
      return generation.generatedTopic;
    }
    const meta = generation.streamingMetadata;
    return {
      id: streamingTopicBase.id,
      title: meta?.title || streamingTopicBase.title,
      description: meta?.description || "",
      language: streamingTopicBase.language,
      difficulty: streamingTopicBase.difficulty,
      speaker: meta?.speaker,
      originalPrompt: streamingTopicBase.originalPrompt,
      vocabulary: generation.intermediateVocabulary || EMPTY_VOCABULARY,
      grammar: [],
      phrases: [],
      dialogNodes: generation.streamingNodes.filter((n): n is DialogNode => n != null),
    };
  }, [streamingTopicBase, generation.generatedTopic, generation.streamingMetadata,
      generation.streamingNodes, generation.intermediateVocabulary]);

  // When generation completes, save the final topic and clear streaming state
  useEffect(() => {
    if (!generation.generatedTopic || !streamingTopicBase) return;
    // Avoid double-handling (React strict mode, etc.)
    if (completionHandledRef.current === streamingTopicBase.id) return;
    completionHandledRef.current = streamingTopicBase.id;

    const finalTopic = generation.generatedTopic;
    const replacingId = streamingTopicBase.replacingTopicId;

    // Save to custom topics with language metadata (skip curriculum adaptations)
    const isAdaptation = streamingTopicBase.id.startsWith("adapt-");
    const topicWithMeta: TopicWithMeta = {
      ...finalTopic,
      _generatedFor: {
        targetLanguage: targetLanguage.code,
        nativeLanguage: nativeLanguage.code,
      },
    };
    if (!isAdaptation) {
      setCustomTopics((prev) => {
        let updated = replacingId ? prev.filter((t) => t.id !== replacingId) : prev;
        const exists = updated.some((t) => t.id === finalTopic.id);
        if (exists) {
          return updated.map((t) => (t.id === finalTopic.id ? topicWithMeta : t));
        }
        return [topicWithMeta, ...updated];
      });
    }
    setSelectedTopic(topicWithMeta);
    setStreamingTopicBase(null);
  }, [generation.generatedTopic, streamingTopicBase, targetLanguage.code, nativeLanguage.code]);

  // Handle starting generation from CustomTopicGenerator
  const handleStartGeneration = useCallback((
    params: GenerationParams,
    topicBase: StreamingTopicBase,
  ) => {
    completionHandledRef.current = null;
    setStreamingTopicBase(topicBase);
    setSelectedTopic(null); // clear any previous topic
    setView("learning");
    // Fire-and-forget — streaming state drives the UI
    generation.generate(params);
  }, [generation]);

  // Handle cancelling generation (back from learning view during streaming)
  const handleCancelGeneration = useCallback(() => {
    generation.cancel();
    setStreamingTopicBase(null);
    setView("main");
  }, [generation]);

  // Handle adaptation of a curriculum topic for the user's native language
  const handleAdaptTopic = useCallback((topicMeta: CurriculumTopicMeta) => {
    completionHandledRef.current = null;
    const topicBase: StreamingTopicBase = {
      id: `adapt-${topicMeta.id}-${Date.now()}`,
      title: topicMeta.titleNative || topicMeta.title,
      originalPrompt: topicMeta.id,
      language: targetLanguage.code,
      difficulty: topicMeta.difficulty,
      richness: "standard" as Richness,
    };
    setStreamingTopicBase(topicBase);
    setSelectedTopic(null);
    setView("learning");
    generation.adaptCurriculum({
      topicId: topicMeta.id,
      targetLanguage: targetLanguage.code,
      nativeLanguage: nativeLanguage.code,
      knownLanguages: userProfile.knownLanguages || undefined,
      ttsSpeed: tts.speed,
      userName: userProfile.name || undefined,
      userGender: userProfile.gender || undefined,
    });
  }, [generation, targetLanguage.code, nativeLanguage.code, userProfile.knownLanguages, userProfile.name, userProfile.gender, tts.speed]);

  // Filter custom topics by current target language
  const baseCode = getBaseLanguageCode(targetLanguage.code);
  const filteredCustomTopics = customTopics.filter(
    (t) => t._generatedFor && getBaseLanguageCode(t._generatedFor.targetLanguage) === baseCode
  );

  // Only show sample topics for European Portuguese (they're in pt-PT)
  // Custom topics appear first, then sample topics
  const isPortuguese = targetLanguage.code.startsWith("pt-");
  const allTopics = isPortuguese
    ? [...filteredCustomTopics, ...sampleTopics]
    : filteredCustomTopics;

  // Aggregate vocabulary across all dialogues for practice tab, deduplicating by baseForm
  const aggregatedVocabulary = useMemo(() => {
    const seenWords = new Set<string>();
    const words: typeof allTopics[0]["vocabulary"]["words"] = [];
    const seenExpressions = new Set<string>();
    const expressions: typeof allTopics[0]["vocabulary"]["expressions"] = [];
    const seenTopical = new Set<string>();
    const topical: typeof allTopics[0]["vocabulary"]["topical"] = [];

    for (const topic of allTopics) {
      if (!topic.vocabulary) continue;
      for (const w of topic.vocabulary.words) {
        const key = w.baseForm.toLowerCase();
        if (!seenWords.has(key)) {
          seenWords.add(key);
          words.push(w);
        }
      }
      for (const e of topic.vocabulary.expressions) {
        const key = e.expression.toLowerCase();
        if (!seenExpressions.has(key)) {
          seenExpressions.add(key);
          expressions.push(e);
        }
      }
      for (const t of topic.vocabulary.topical) {
        const key = t.category.toLowerCase();
        if (!seenTopical.has(key)) {
          seenTopical.add(key);
          topical.push(t);
        }
      }
    }
    return { words, expressions, topical };
  }, [allTopics]);

  const handleSelectTopic = (topic: DialogTopic) => {
    setSelectedTopic(topic);
    setView("learning");
  };

  const handleDeleteTopic = useCallback((topicId: string) => {
    // Remove from custom topics
    setCustomTopics((prev) => {
      const updated = prev.filter((t) => t.id !== topicId);
      // Update localStorage
      if (updated.length > 0) {
        localStorage.setItem(TOPICS_STORAGE_KEY, JSON.stringify(updated));
      } else {
        localStorage.removeItem(TOPICS_STORAGE_KEY);
      }
      return updated;
    });
    // Navigate back
    setSelectedTopic(null);
    setView("main");
  }, []);

  const handleRegenerateTopic = useCallback(() => {
    if (!selectedTopic) return;
    // Delete the current topic first
    setCustomTopics((prev) => {
      const updated = prev.filter((t) => t.id !== selectedTopic.id);
      if (updated.length > 0) {
        localStorage.setItem(TOPICS_STORAGE_KEY, JSON.stringify(updated));
      } else {
        localStorage.removeItem(TOPICS_STORAGE_KEY);
      }
      return updated;
    });
    // Store the topic data and auto-generate
    setInitialTopicData({
      topic: selectedTopic.originalPrompt || selectedTopic.title,
      difficulty: selectedTopic.difficulty,
      autoGenerate: true,
    });
    setSelectedTopic(null);
    setView("custom-generator");
  }, [selectedTopic]);

  const handleEditTopic = useCallback(() => {
    if (!selectedTopic) return;
    // Don't delete yet — only replace when new topic is loaded
    setInitialTopicData({
      topic: selectedTopic.originalPrompt || selectedTopic.title,
      difficulty: selectedTopic.difficulty,
      autoGenerate: false, // Show wizard for editing
      replacingTopicId: selectedTopic.id,
    });
    setSelectedTopic(null);
    setView("custom-generator");
  }, [selectedTopic]);

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback((profile: Parameters<typeof completeOnboarding>[0], analyticsEnabled: boolean) => {
    // Set analytics opt-out based on user choice (opt-out is inverse of enabled)
    setAnalyticsOptOut(!analyticsEnabled);

    // Complete onboarding
    completeOnboarding(profile);

    // Jump straight into topic creation after onboarding
    setView("custom-generator");

    // Track onboarding completion
    track(events.ONBOARDING_COMPLETED, {
      name: profile.name ? "provided" : "skipped",
      style: profile.speakingStyle || "skipped",
      known_languages_count: profile.knownLanguages.length,
      analytics_opted_in: analyticsEnabled,
    });
  }, [completeOnboarding, setAnalyticsOptOut, track, events, targetLanguage.code, nativeLanguage.code]);

  // Show onboarding wizard on first launch
  if (!hasCompletedOnboarding) {
    return (
      <main className="min-h-screen bg-background">
        <OnboardingWizard onComplete={handleOnboardingComplete} />
      </main>
    );
  }

  // When in learning view or custom generator, don't show bottom nav
  const activeTopic = selectedTopic || streamingTopic;
  const isStreaming = generation.isGenerating && !!streamingTopicBase;

  if (view === "learning" && activeTopic) {
    // Only allow delete/edit/regenerate for finalized custom topics (not during streaming)
    const isCustomTopic = !isStreaming && customTopics.some((t) => t.id === activeTopic.id);

    return (
      <main className="min-h-screen bg-background">
        <LearningView
          topic={activeTopic}
          onBack={isStreaming ? handleCancelGeneration : () => {
            setSelectedTopic(null);
            setView("main");
          }}
          onDelete={isCustomTopic ? handleDeleteTopic : undefined}
          onRegenerate={isCustomTopic ? handleRegenerateTopic : undefined}
          onEdit={isCustomTopic ? handleEditTopic : undefined}
          isStreaming={isStreaming}
          streamingProgress={generation.progress}
          streamingError={generation.error}
          richness={streamingTopicBase?.richness}
          ttsReadyTexts={generation.ttsReadyTexts}
          flyMachineId={generation.flyMachineId}
        />
      </main>
    );
  }

  if (view === "custom-generator") {
    return (
      <main className="min-h-screen bg-background">
        <CustomTopicGenerator
          onBack={() => {
            setInitialTopicData(null);
            setView("main");
          }}
          onStartGeneration={(params, topicBase) => {
            const replacingId = initialTopicData?.replacingTopicId;
            setInitialTopicData(null);
            handleStartGeneration(params, { ...topicBase, replacingTopicId: replacingId });
          }}
          onOpenSettings={() => setView("settings")}
          initialTopic={initialTopicData}
        />
      </main>
    );
  }

  if (view === "settings") {
    return (
      <main className="min-h-screen bg-background">
        <SettingsScreen onBack={() => setView("main")} lastCost={generation.lastCost} />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background flex flex-col">
      {/* Main Content */}
      <div className="flex-1 pb-24">
        {mainTab === "topics" && (
          <TopicSelector
            topics={allTopics}
            onSelectTopic={handleSelectTopic}
            onAdaptTopic={handleAdaptTopic}
            onCreateCustom={() => setView("custom-generator")}
            onOpenSettings={() => setView("settings")}
          />
        )}
        {mainTab === "practice" && (
          aggregatedVocabulary.words.length > 0 ? (
            <PracticeSection vocabulary={aggregatedVocabulary} language={targetLanguage.code} />
          ) : (
            <div className="flex flex-col min-h-screen">
              <header className="sticky top-0 z-10 bg-card shadow-[0_4px_16px_rgba(255,255,255,0.9)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)] px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-lg font-semibold text-foreground">{t.nav.practice}</h1>
                  </div>
                  <button
                    type="button"
                    onClick={() => setView("settings")}
                    className="w-10 h-10 rounded-xl hover:bg-muted flex items-center justify-center transition-colors"
                  >
                    <Settings className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </header>
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">{t.learning.noWordsToReview}</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t.learning.noWordsToReviewDesc}
                </p>
              </div>
            </div>
          )
        )}
        {mainTab === "alphabet" && (
          <div className="flex flex-col min-h-screen">
            <header className="sticky top-0 z-10 bg-card shadow-[0_4px_16px_rgba(255,255,255,0.9)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)] px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <Languages className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <h1 className="text-lg font-semibold text-foreground">{t.alphabet.title}</h1>
                  <p className="text-xs text-muted-foreground">{t.alphabetSubtitle}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setView("settings")}
                  className="w-10 h-10 rounded-xl hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Settings className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
            </header>
            {currentAlphabet ? (
              <AlphabetSection alphabet={currentAlphabet} />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Languages className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">{t.alphabet.comingSoonTitle}</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t.alphabetComingSoonDescription}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation - iOS 26 style floating pill */}
      <nav className="fixed bottom-4 left-4 right-4 safe-area-bottom">
        <div className="mx-auto max-w-md bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.5)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
          <div className="flex items-center justify-around h-14 px-2">
            <button
              type="button"
              onClick={() => setMainTab("topics")}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all",
                mainTab === "topics"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-xs font-medium">{t.nav.dialogues}</span>
            </button>
            <button
              type="button"
              onClick={() => setMainTab("practice")}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all",
                mainTab === "practice"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <BookOpen className="w-5 h-5" />
              <span className="text-xs font-medium">{t.nav.practice}</span>
            </button>
            <button
              type="button"
              onClick={() => setMainTab("alphabet")}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all",
                mainTab === "alphabet"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <Languages className="w-5 h-5" />
              <span className="text-xs font-medium">{t.nav.alphabet}</span>
            </button>
          </div>
        </div>
      </nav>
    </main>
  );
}
